import React from 'react';
import { AssetTypeFieldDef } from '../../types';
import './FieldRenderer.css';

interface FieldRendererProps {
  field: AssetTypeFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
}

/** Render an input appropriate for one registry field definition. */
export const FieldRenderer: React.FC<FieldRendererProps> = ({ field, value, onChange }) => {
  const inputId = `asset-field-${field.key}`;
  const stringValue = value === null || value === undefined ? '' : String(value);
  let control: React.ReactNode;

  if (field.type === 'boolean') {
    control = (
      <input
        id={inputId}
        type="checkbox"
        checked={Boolean(value)}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  } else if (field.type === 'select') {
    control = (
      <select id={inputId} value={stringValue} required={field.required} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select…</option>
        {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  } else {
    const numeric = field.type === 'number' || field.type === 'temperature' || field.type === 'percentage';
    const suffix = field.type === 'percentage' ? '%' : field.type === 'temperature' ? (field.unit || '°C') : field.unit;
    control = (
      <div className={suffix ? 'registry-field-input-with-unit' : undefined}>
        <input
          id={inputId}
          type={field.type === 'date' ? 'date' : numeric ? 'number' : 'text'}
          value={stringValue}
          required={field.required}
          min={field.type === 'percentage' ? 0 : undefined}
          max={field.type === 'percentage' ? 100 : undefined}
          onChange={(event) => onChange(numeric && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
        />
        {suffix && <span className="registry-field-unit">{suffix}</span>}
      </div>
    );
  }

  return (
    <div className={`registry-field registry-field-${field.type}`}>
      <label htmlFor={inputId}>{field.label}{field.required ? ' *' : ''}</label>
      {control}
      {field.help && <small>{field.help}</small>}
    </div>
  );
};

interface FieldGroupRendererProps {
  field_schema: AssetTypeFieldDef[];
  attributes: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

/** Group dynamic asset fields into ordered, headed sections. */
export const FieldGroupRenderer: React.FC<FieldGroupRendererProps> = ({ field_schema, attributes, onChange }) => {
  const sectionNames = field_schema.reduce<string[]>((names, field) => {
    const section = field.section || 'Details';
    if (section !== 'Details' && names.indexOf(section) === -1) names.push(section);
    return names;
  }, []);
  if (field_schema.some((field) => !field.section)) sectionNames.push('Details');

  return <div className="registry-field-groups">{sectionNames.map((section) => (
    <section key={section} className="registry-field-group">
      <h4>{section}</h4>
      {field_schema.filter((field) => (field.section || 'Details') === section).map((field) => (
        <FieldRenderer key={field.key} field={field} value={attributes[field.key]} onChange={(value) => onChange(field.key, value)} />
      ))}
    </section>
  ))}</div>;
};

export default FieldRenderer;
